/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable react/jsx-no-target-blank */
import { constants } from 'ethers';
import React, { useContext, useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { ReactComponent as CrownIcon } from '../../common/svg/movie-voting/crown_icon.svg';
import { ReactComponent as OpenSeaIcon } from '../../common/svg/movie-voting/opensea_icon.svg';
import { ReactComponent as VotedWhiteIcon } from '../../common/svg/movie-voting/voted_white_icon.svg';
import { ReactComponent as VotingIcon } from '../../common/svg/movie-voting/voting_icon.svg';
import type { ProjectMetadata } from '../../containers/pages/project/ProjectPage.container';
import { ModalStateContext } from '../../contexts/app-state-context/modal-state.context';
import { useWeb3State } from '../../hooks';
import { useMovieVotingContract } from '../../hooks/movie-voting.hooks';
import { useStarsContract } from '../../hooks/stars-contract.hooks';
import { useVotingContract } from '../../hooks/voting.hooks';
import { CHAIN_ID_TO_KEY_MAP, WEB3_CONFIG } from '../../lib';
import type { VoteModalProps } from '../common/modal/movie-voting/VoteModal.component';
import { Button, Colors } from '../legacy/button/Button.component';
import type { CastAndCrewData } from '../movie/card/AdditionalInfo.component';
import type { VotingCardProps } from '../movie/card/VotingCard.component';
import { createWithdrawStarsModal } from '../movie/modal/WithdrawStarsModal.component';
import styles from './ProjectVoting.module.css';

interface Props extends VotingCardProps {
  movieIds: string[];
  roundId: string;
  userVotes: {
    [movieId: string]: string;
  };
  cast: Record<string, CastAndCrewData[]>;
  totalVotes: string[];
  movieMetadata: {
    [key: string]: ProjectMetadata;
  };
  handleViewOnClick: (movieId: string) => void;
  handleVoteOnClick: (props: VoteModalProps) => void;
  setUpVoting: () => void;

  openseaLink: string;
  noVoteButton?: boolean;
  isEnded?: boolean | undefined;
  displayAsCarousel?: boolean;
}

export const ProjectVoting: React.FC<Props> = props => {
  const { walletDetails } = useWeb3State();
  const { setModal } = useContext(ModalStateContext);

  const {
    totalVotes,
    movieIds,
    roundId,
    movieMetadata,
    openseaLink,
    noVoteButton,
    userVotes,
    cast,
    handleViewOnClick,
    handleVoteOnClick,
    setUpVoting,
    isEnded,
    displayAsCarousel = false,
    ...voteModalProps
  } = props;
  const starsContract = useStarsContract();

  const movieVotingContract = useMovieVotingContract({
    overrideAddress: WEB3_CONFIG.contracts.eth.movieVoting,
    overrideProvider: CHAIN_ID_TO_KEY_MAP[WEB3_CONFIG.chains.eth.id],
    hardOverride: false,
  });

  const votingContract = useVotingContract({
    overrideAddress: WEB3_CONFIG.contracts.bsc.movieVoting,
    overrideProvider: CHAIN_ID_TO_KEY_MAP[WEB3_CONFIG.chains.bsc.id],
    hardOverride: false,
  });

  const {
    initialized,
    getUserStarsForMovie,
    calculateStarsRewards,
    vote,
    removeVotes,
  } =
    walletDetails.chain.id === WEB3_CONFIG.chains.eth.id
      ? movieVotingContract
      : votingContract;

  const [totalStarsEntered, setTotalStarsEntered] = useState<
    Record<string, string>
  >({});
  const [totalRewards, setTotalRewards] = useState('');
  const [loading, setLoading] = useState(true);
  let winnerIndex = 0;
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [showNavigation, setShowNavigation] = useState<boolean>(false);
  // Responsiveness
  useEffect(() => {
    const changeWidth = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', changeWidth);

    return () => {
      window.removeEventListener('resize', changeWidth);
    };
  }, []);

  useEffect(() => {
    if (screenWidth < 500) {
      setShowNavigation(true);
    } else {
      setShowNavigation(false);
    }
  }, [screenWidth]);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    void (async () => {
      const totalStars: Record<string, string> = {};
      for (const movieId of movieIds) {
        totalStars[movieId] = await getUserStarsForMovie(roundId, movieId);
      }

      const rewards = await calculateStarsRewards(roundId);

      setTotalStarsEntered(totalStars);
      setTotalRewards(rewards);

      setLoading(false);
    })();
  }, [initialized]);

  const winningMovieVotes = totalVotes.reduce((a, b) => {
    return parseInt(a, 10) > parseInt(b, 10) ? a : b;
  });

  if (loading) {
    return <></>;
  }

  const movieComponent = (movieId: string, i: number) => {
    const data = movieMetadata[movieId];
    const isWinner =
      parseInt(winningMovieVotes, 10) !== 0 &&
      winningMovieVotes === totalVotes[i];
    if (isWinner) {
      winnerIndex = i;
    }

    const handlePlaceVote = async (starsAmount: string) => {
      const allowance = await starsContract.allowance(
        walletDetails.address ?? '',
        WEB3_CONFIG.contracts[walletDetails.chain.key].movieVotingChef,
      );

      let result;
      if (parseFloat(allowance) < parseFloat(starsAmount)) {
        result = await starsContract.approve(
          WEB3_CONFIG.contracts[walletDetails.chain.key].movieVotingChef,
          constants.MaxUint256,
        );
      } else {
        result = await vote(roundId, movieId, starsAmount);
      }

      if (result) {
        setUpVoting();
      }

      return result;
    };

    const handleClickWithdrawStars = async (starsAmt: string) => {
      const result = await removeVotes(roundId, movieId, starsAmt);

      if (result) {
        setUpVoting();
      }

      return result;
    };

    const handleWithdrawStars = () => {
      setModal(
        createWithdrawStarsModal({
          totalStarsEntered: totalStarsEntered[movieId],
          totalRewards,
          withdrawStars: handleClickWithdrawStars,
          rewardsPerBlock: voteModalProps.rewardsPerBlock,
        }),
      );
    };

    const movieActions = (
      <div className="flex">
        {noVoteButton === true ? (
          <div className="flex w-full">
            <Button
              className={'flex w-full h-10'}
              color={Colors.COLOR_SCHEME_6}
              onClick={() => {
                handleViewOnClick(movieId);
              }}
            >
              VIEW DETAILS
            </Button>
          </div>
        ) : (
          <div className="flex w-full">
            <Button
              className={'flex-initial h-10 mr-3'}
              color={Colors.COLOR_SCHEME_6}
              onClick={() => {
                handleViewOnClick(movieId);
              }}
            >
              VIEW
            </Button>
            <Button
              className={'flex-1 h-10'}
              color={Colors.COLOR_SCHEME_2}
              onClick={() =>
                handleVoteOnClick({
                  image: data.image,
                  name: data.name,
                  synopsis: data.description,
                  studio: data.studio,
                  ...voteModalProps,
                  cast: cast[movieId],
                  numberOfVotes: totalVotes[i],
                  placeVote: handlePlaceVote,
                  withdrawStars: handleWithdrawStars,
                  starsSubmitted: totalStarsEntered[movieId],
                  rewardGenerated: totalRewards,
                })
              }
            >
              VOTE
            </Button>
          </div>
        )}
      </div>
    );

    return (
      <div
        key={`${data.name}${i.toString()}`}
        className={`rounded-lg flex flex-col justify-between
          ${!displayAsCarousel ? styles.item : ''}
          ${
            displayAsCarousel
              ? movieIds.length % 2 === 0
                ? styles.movieBackground + ' '
                : styles.movieBackgroundOdd +
                  ' 2xl:w-72 w-full bg-gradient-to-r '
              : movieIds.length % 2 === 0
              ? styles.movieBackground + ' '
              : styles.movieBackgroundOdd +
                ' 2xl:w-72 lg:w-60 w-full sm:h-auto bg-gradient-to-r '
          }
          ${isWinner && !displayAsCarousel ? styles.winningBackground : ''}
        `}
        style={{
          backgroundColor: '#161a27',
          backgroundImage: `${
            movieIds.length % 2 === 0
              ? 'linear-gradient(to bottom,rgba(255,255,255,0.01) 0%, rgba(0,0,0,0.1) 50%, #161a27 80%), '
              : 'linear-gradient(to bottom,rgba(255,255,255,0.01) 0%, rgba(0,0,0,0.1) 50%, #161a27 75%), '
          }
            url(
            ${data.image}
            )`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center top',
        }}
      >
        <div className="flex items-end justify-end">
          <a href={`${openseaLink}${movieId}`} target="_blank">
            <OpenSeaIcon
              className={`relative right-3 top-3 pointer bg-gray-400 rounded-full`}
            />
          </a>
        </div>
        <div className="h-56 flex flex-col justify-end px-3 pb-3">
          <div className="flex relative bottom-3">
            {isWinner && (
              <div className={styles.winning}>
                <CrownIcon className="mr-1" />
                {noVoteButton === true ? 'Winner' : 'Winning'}
              </div>
            )}
            {parseInt(userVotes[movieId], 10) > 0 && (
              <div className={styles.voted}>
                <VotedWhiteIcon className="mr-1" />
                You voted
              </div>
            )}
          </div>
          <div className={styles.movieTitle + ' uppercase mb-3'}>
            {data.name}
          </div>
          <div className={styles.votingText + ' flex items-center mb-5'}>
            <VotingIcon className="mr-1" />
            {parseInt(totalVotes[i], 10).toLocaleString()} Votes
          </div>
          {movieActions}
        </div>
      </div>
    );
  };

  const moviesToShowCarousel = movieIds.map((movieId, i) => {
    return (
      <SwiperSlide className={styles.swiperSlide} key={i}>
        {movieComponent(movieId, i)}
      </SwiperSlide>
    );
  });

  const moviesToShow = movieIds.map((movieId, i) => {
    return <>{movieComponent(movieId, i)}</>;
  });

  return (
    <>
      {!displayAsCarousel ? (
        <div
          className={`flex flex-wrap ${
            isEnded ? 'xl:justify-center' : 'xl:justify-between'
          } justify-center lg:gap-8`}
        >
          {!isEnded ? moviesToShow : moviesToShow[winnerIndex]}
        </div>
      ) : (
        <Swiper
          navigation={showNavigation}
          freeMode={true}
          freeModeMinimumVelocity={0.4}
          freeModeMomentum={true}
          style={{
            maxHeight: 'unset',
            justifyContent: 'start',
            paddingTop: '10px',
          }}
          className={styles.swiperContainer}
          direction="horizontal"
          grabCursor={true}
          preventClicksPropagation={true}
          preventClicks={true}
          breakpoints={{
            200: {
              slidesPerView: 1,
            },
            500: {
              slidesPerView: 1.5,
              spaceBetween: 20,
            },
            640: {
              slidesPerView: 1.5,
              spaceBetween: 20,
            },
            800: {
              slidesPerView: 1.5,
              spaceBetween: 20,
            },
            1000: {
              slidesPerView: 2.1,
              spaceBetween: 20,
            },
            1250: {
              slidesPerView: 3.2,
              spaceBetween: 30,
            },
          }}
        >
          <div
            className={`flex flex-wrap justify-center 2xl:gap-10 xl:gap-14 lg:gap-8`}
          >
            {!isEnded ? moviesToShowCarousel : moviesToShow[winnerIndex]}
          </div>
        </Swiper>
      )}
    </>
  );
};
